import { useCallback, useMemo, useState } from "react";
import SideDrawer, { SideDrawerBody } from "../../../components/SideDrawer";
import { Box, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import useEventEmitter, { emitEvent } from "../../../hooks/useEventEmitter";
import { useForm } from "react-hook-form";
import { detectChange } from "../../../helpers/detectChange";
import useEditTimerLabelMutation from "../hooks/useEditTimerLabelMutation";
import isNotNull from "../../../helpers/isNotNull";
import { fireSaveAndMoveNextTimelineEvent } from "./SaveAndMoveNextTimeline";
import { formatMilliSeconds } from "../../../helpers/formatMilliSeconds";
import formatDateTime from "../../../helpers/formatDateTime";
import { DATE_TYPE } from "../../../utils/Types";
import InputText from "../../../components/InputText";
import EditCaseSummaryDrawerFooter from "./EditCaseSummaryDrawerFooter";

const EDIT_TIMER_LABEL_DRAWER_EVENT = "EDIT_TIMER_LABEL_DRAWER_EVENT";

export type EditTimerLabelDrawerProps = {
  caseId?: number;
  timerId?: number;
  description?: string | null;
  timerDuration?: number;
  timerCreatedAt?: DATE_TYPE | null;
  timelineItemIndex?: number;
};

export function toggleEditTimerLabelDrawer(data?: EditTimerLabelDrawerProps) {
  emitEvent(EDIT_TIMER_LABEL_DRAWER_EVENT, data);
}

const EditTimerLabelDrawer = () => {
  const { t } = useTranslation();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<EditTimerLabelDrawerProps>();
  const {
    timelineItemIndex,
    description,
    caseId,
    timerId,
    timerDuration,
    timerCreatedAt,
  } = detail ?? {};

  const { mutate: editTimerLabelMutate, isPending: isEditingTimerLabel } =
    useEditTimerLabelMutation();

  const { control, watch, reset, handleSubmit } = useForm();

  const onClose = useCallback(() => {
    reset();
    setIsDrawerOpen(false);
  }, [reset]);

  const setFormData = useCallback(
    (data?: EditTimerLabelDrawerProps) => {
      const formDefaultValues = {
        description: data?.description ?? "",
      };
      reset(formDefaultValues);
    },
    [reset]
  );

  useEventEmitter(
    EDIT_TIMER_LABEL_DRAWER_EVENT,
    (data?: EditTimerLabelDrawerProps) => {
      setIsDrawerOpen((prev) => !prev);
      setDetail(data);
      setFormData(data);
    }
  );

  const isSubmitDisabled = useMemo(() => {
    const isDescriptionChanged = detectChange(
      description,
      watch("description")
    );
    return (
      !watch("description") || !isDescriptionChanged || isEditingTimerLabel
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("description"), description]);

  const onSubmit = (isSaveAndMoveNext?: boolean) =>
    handleSubmit((val) => {
      if (caseId && timerId) {
        editTimerLabelMutate(
          {
            caseId,
            timerId,
            timerData: {
              description: val?.description,
            },
          },
          {
            onSuccess: (res) => {
              onClose();
              if (isSaveAndMoveNext) {
                isNotNull(timelineItemIndex) &&
                  fireSaveAndMoveNextTimelineEvent(timelineItemIndex);
              }
            },
          }
        );
      }
    })();

  return (
    <SideDrawer
      visible={isDrawerOpen}
      onClose={onClose}
      title={t("Edit_Timer_Label")}
    >
      <SideDrawerBody>
        {/* Body */}
        <Box sx={styles.priorBanner}>
          <Typography sx={styles.title}>
            {t("Edit_Timer_Label_Desc", {
              duration: formatMilliSeconds(timerDuration),
            })}
            {" at "}
            <Typography component="span" fontWeight={700}>
              {formatDateTime(timerCreatedAt, true, "HH:mm")}
            </Typography>
          </Typography>
        </Box>

        {/* Content */}
        <InputText
          control={control}
          name="description"
          label={t("Timer_Label")}
          isLabelDefaultBehaviour
          sx={{ borderRadius: 1 }}
        />

        {/* Footer */}
        <Divider sx={{ mt: 8, mb: 2, mx: -3 }} />
        <EditCaseSummaryDrawerFooter
          onSubmit={onSubmit}
          isSubmitDisabled={isSubmitDisabled}
        />
      </SideDrawerBody>
    </SideDrawer>
  );
};

const styles = {
  title: {
    fontSize: 18,
  },
  priorBanner: {
    display: "flex",
    alignItems: "center",
    px: 2,
    py: 1.25,
    mb: 3,
    backgroundColor: "#E6E6E6",
    borderLeft: "4px solid #5921F2",
  },
};

export default EditTimerLabelDrawer;
